import { cn } from "@/lib/cn"
import { toneCss } from "../format"
import type { CardListBlock, CardListItem } from "../types"

// Stacked card list — alternative to <table> for narrow layouts. Each
// item becomes a small card with optional title/subtitle/badge plus a
// label-value field grid. Reflows naturally at any width because there
// are no columns to fit; cards stack vertically and fields wrap inside.
//
// Use this when the data has heterogeneous fields per row OR when the
// half-page sidebar would otherwise force a 9-column table to clip /
// scroll horizontally. Reads well on mobile and desktop alike.
export function CardListBlockView({ block }: { block: CardListBlock }) {
  const items = Array.isArray(block.items) ? block.items : []
  const max = block.max_items ?? items.length
  const visible = items.slice(0, max)
  const hidden = items.length - visible.length

  if (visible.length === 0) {
    return (
      <div className="rounded-card border border-border bg-app-panel2 px-4 py-6 text-center text-sm text-app-muted">
        {block.empty_text || "No data."}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <ul className="space-y-2">
        {visible.map((item, idx) => (
          <CardItem key={idx} item={item} />
        ))}
      </ul>
      {hidden > 0 && (
        <p className="text-xs text-app-muted">
          {hidden} more not shown.
        </p>
      )}
    </div>
  )
}

function CardItem({ item }: { item: CardListItem }) {
  const fields = Array.isArray(item.fields) ? item.fields : []
  const hasHeader = item.title || item.subtitle || item.badge
  return (
    <li className="rounded-control border border-border bg-app-panel2 p-3">
      {hasHeader && (
        <header className="mb-2 flex items-baseline justify-between gap-2">
          <div className="min-w-0 flex-1">
            {item.title && (
              <div className="truncate text-sm font-medium text-app-text">{item.title}</div>
            )}
            {item.subtitle && (
              <div className="truncate text-xs text-app-muted">{item.subtitle}</div>
            )}
          </div>
          {item.badge && <CardBadge label={item.badge.label} tone={item.badge.tone} />}
        </header>
      )}
      {fields.length > 0 && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
          {fields.map((f, idx) => (
            <FieldRow key={`${f.label}-${idx}`} field={f} />
          ))}
        </dl>
      )}
    </li>
  )
}

function FieldRow({ field }: { field: CardListItem["fields"][number] }) {
  const v = field.value
  const display =
    v === null || v === undefined || v === ""
      ? null
      : typeof v === "boolean"
        ? v ? "Yes" : "No"
        : String(v)
  return (
    <>
      <dt className="text-app-subtle">{field.label}</dt>
      <dd className={cn("min-w-0 break-words text-app-text", field.mono && "font-mono text-[11px]")}>
        {display === null ? <span className="text-app-subtle">—</span> : display}
      </dd>
    </>
  )
}

function CardBadge({ label, tone }: { label: string; tone: CardListItem["badge"] extends infer B ? (B extends { tone: infer T } ? T : never) : never }) {
  const css = toneCss(tone)
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-chip border px-1.5 py-0.5 text-[10px] font-medium",
        css.text, css.bg, css.border,
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", css.dot)} aria-hidden="true" />
      {label}
    </span>
  )
}
